'use client';

import GlobeIcon from '@/components/Icons/Globe';
import PeopleOutlineIcon from '@/components/Icons/PeopleOutline';
import TimerIcon from '@/components/Icons/Timer';
import { useTranslation } from '@/contexts/dictionaryContext';
import { Experience, ExperienceTranslation } from '@/services/experienceTypes';
import { FC, HTMLAttributes } from 'react';
import { MdPlace } from 'react-icons/md';
import ExperienceRating from './ExperienceRating';

interface IExperienceSectionsProps extends HTMLAttributes<HTMLDivElement> {
  translation?: ExperienceTranslation;
  experience: Experience;
}

export const ExperienceInfo: FC<IExperienceSectionsProps> = ({
  translation,
  experience,
}) => {
  const { experienceInfo: dict } = useTranslation();

  const participants =
    experience.minParticipants === experience.maxParticipants
      ? `${experience.maxParticipants} ${dict.people}`
      : `${experience.minParticipants} - ${experience.maxParticipants} ${dict.people}`;

  return (
    <div className="flex flex-col desktop:flex-row gap-4 desktop:gap-0 desktop:h-[182px] mb-[30px] sm:mb-[50px] desktop:mb-20 px-sm md:px-md desktop:px-lg w-full max-w-default desktop:mx-auto">
      <div className="flex flex-col gap-4 desktop:w-[410px]">
        <h1 className="text-title font-title">{translation?.title}</h1>
        <div className="flex items-center text-gray-700">
          <MdPlace className="w-5 h-5 mr-1" />
          {experience.id === 36 ? (
            <p>{`(${dict.sicily})`}</p>
          ) : (
            <p className="text-base">
              {experience.city ? experience.city : dict.yourVilla}
              {experience.zone &&
                ` (${
                  dict.zone[experience.zone.code as keyof typeof dict.zone]
                })`}
            </p>
          )}
        </div>
        {experience.countReviews > 0 && (
          <ExperienceRating
            dict={dict}
            averageScore={experience.avgReviews}
            countReviews={experience.countReviews}
          />
        )}
      </div>
      <div className="flex-1 flex border border-gray-200 bg-gray-100 min-h-[150px] desktop:ml-[30px] mb-4">
        <div className="flex flex-col justify-center items-center h-full w-1/3 py-8 px-2 gap-2">
          <div>
            <TimerIcon className="w-[45px] h-[45px]" color="#004d9d" />
          </div>
          <div className="text-xl text-center font-averta-semibold">
            {experience.duration && experience.duration !== '-'
              ? `${experience.duration} ${
                  experience.macroCategory.id === 54 ? dict.hour : dict.hours
                }`
              : '-'}
          </div>
        </div>
        <div className="flex flex-col justify-center items-center h-full w-1/3 py-8 px-2 gap-2">
          <div>
            <GlobeIcon className="w-[45px] h-[45px]" color="#004d9d" />
          </div>
          <div className="text-xl text-center font-averta-semibold">
            {experience?.languages.map((lang) => lang.description).join(', ')}
          </div>
        </div>
        <div className="flex flex-col justify-center items-center h-full w-1/3 py-8 px-2 gap-2">
          <div>
            <PeopleOutlineIcon className="w-[45px] h-[45px]" color="#004d9d" />
          </div>
          <div className="text-xl text-center font-averta-semibold">
            {participants}
          </div>
        </div>
      </div>
    </div>
  );
};
